import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Copy, RefreshCw, X, Plus, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { copyText, oapi } from '@/lib/officialsApi';
import { SettingsCard, useSaver } from './SettingsCard';

export const JoinLinkCard = ({ link, save }) => {
  const [confirm, setConfirm] = useState(false);
  const [busy, run] = useSaver(save);
  if (!link) return null;
  return (
    <SettingsCard title="Officials sign-up link" desc="Share this link so officials can add themselves to your list with a free account." testId="settings-join-link">
      <div className="flex gap-2">
        <Input readOnly value={link} className="h-9 text-xs font-mono" data-testid="join-link-input" onFocus={e => e.target.select()} />
        <Button variant="outline" size="sm" className="h-9" onClick={() => copyText(link, 'Sign-up link')} data-testid="join-link-copy"><Copy className="w-4 h-4" /></Button>
      </div>
      <div className="flex items-center gap-2 mt-3">
        {confirm ? (
          <>
            <span className="text-xs text-red-600 mr-auto">The old link will stop working.</span>
            <Button variant="outline" size="sm" onClick={() => setConfirm(false)}>Keep</Button>
            <Button size="sm" disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="join-link-regenerate-confirm"
              onClick={() => run({ regenerate_join_link: true }, 'New link created').then(() => setConfirm(false))}>Make new link</Button>
          </>
        ) : (
          <Button variant="ghost" size="sm" className="text-slate-500" onClick={() => setConfirm(true)} data-testid="join-link-regenerate"><RefreshCw className="w-4 h-4 mr-1.5" />Make a new link</Button>
        )}
      </div>
    </SettingsCard>
  );
};

const InviteOptions = ({ invite, setInvite, withManual, setWithManual }) => (
  <div className="mt-3 space-y-2 text-sm text-slate-700" data-testid="scheduler-invite-options">
    <label className="flex items-center gap-2"><Checkbox checked={invite} onCheckedChange={v => setInvite(!!v)} data-testid="scheduler-invite-toggle" />Email them an invite</label>
    <label className={`flex items-center gap-2 pl-6 ${invite ? '' : 'opacity-50'}`}>
      <Checkbox checked={withManual} onCheckedChange={v => setWithManual(!!v)} data-testid="scheduler-invite-manual-toggle" />
      <span>Include a link to the <Link to="/manual/officials#org-overview" className="text-[#00247D] underline" target="_blank">Officials manual</Link></span>
    </label>
  </div>
);

export const SchedulersCard = ({ orgId, schedulers, save }) => {
  const [email, setEmail] = useState('');
  const [invite, setInvite] = useState(true);
  const [withManual, setWithManual] = useState(true);
  const [sending, setSending] = useState('');
  const [busy, run] = useSaver(save);
  const emails = schedulers.map(s => s.email);
  const sendInvite = async (e) => {
    setSending(e);
    try {
      await oapi('/officials/schedulers/invite', { method: 'POST', orgId, body: { email: e, include_manual: withManual } });
      toast.success(`Invite emailed to ${e}${withManual ? ' with the manual link' : ''}`);
    } catch (err) { toast.error(err.message); } finally { setSending(''); }
  };
  const add = async () => {
    const e = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return;
    if (!(await run({ schedulers: [...emails, e] }, `${e} can now schedule officials`))) return;
    setEmail('');
    if (invite) sendInvite(e);
  };
  return (
    <SettingsCard title="Officials schedulers" desc="People who can assign officials and manage your officials list. They sign in with their own free account." testId="settings-schedulers">
      <ul className="space-y-1.5 mb-3">
        {emails.map(e => (
          <li key={e} className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-1.5" data-testid={`scheduler-${e}`}>
            <span className="flex-1 truncate">{e}</span>
            <button type="button" disabled={!!sending} onClick={() => sendInvite(e)} title={withManual ? 'Email invite + manual link' : 'Email invite'}
              className="text-slate-400 hover:text-[#00247D]" data-testid={`scheduler-invite-${e}`}>
              {sending === e ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            </button>
            <button type="button" disabled={busy} onClick={() => run({ schedulers: emails.filter(x => x !== e) }, 'Scheduler removed')} className="text-slate-400 hover:text-red-600" data-testid={`scheduler-remove-${e}`}><X className="w-4 h-4" /></button>
          </li>
        ))}
        {!emails.length && <li className="text-sm text-slate-400">Only you for now.</li>}
      </ul>
      <form className="flex gap-2" onSubmit={e => { e.preventDefault(); add(); }}>
        <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" className="h-9" data-testid="scheduler-email-input" />
        <Button type="submit" size="sm" className="h-9 bg-[#00247D] hover:bg-[#001A5C]" disabled={busy || !email.trim()} data-testid="scheduler-add-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </Button>
      </form>
      <InviteOptions invite={invite} setInvite={setInvite} withManual={withManual} setWithManual={setWithManual} />
    </SettingsCard>
  );
};
